// The longest Payroll ID the backend accepts on a UK payroll (IRIS) tenant.
export const PAYROLL_ID_MAX_LENGTH = 35;

// IRIS (Staffology) accepts only ASCII letters, digits, spaces and hyphens.
const PAYROLL_ID_ALLOWED_CHARACTERS = /^[A-Za-z0-9 -]+$/;

const PAYROLL_ID_SERVER_MESSAGE_PREFIX = 'Payroll ID';

export type PayrollIdErrorKind = 'required' | 'too-long' | 'invalid-characters';

/**
 * A Payroll ID as text without surrounding spaces: null and undefined become ''.
 */
export function normalisePayrollId(value: unknown): string {
  return (value === null || value === undefined ? '' : String(value)).trim();
}

/**
 * Checks a Payroll ID against the three rules the client enforces for UK payroll tenants.
 * The value is trimmed first, so spaces alone count as blank.
 * A blank value is a "required" error only when `required` is true: an existing employee's
 * pay details need one, a new team member may be created without one (the payroll team
 * assigns it before the first pay run).
 * The rules are: not blank, at most 35 characters, and only ASCII letters, digits, spaces and
 * hyphens. The too-long rule is checked before the characters rule.
 * A further rule, "already used by another team member", is only known to the server.
 * Returns the rule that failed, or null when the value is fine.
 */
export function getPayrollIdErrorKind(
  value: unknown,
  required = true
): PayrollIdErrorKind | null {
  const trimmed = normalisePayrollId(value);
  if (!trimmed) {
    return required ? 'required' : null;
  }
  // Count code points, to match the backend's mb_strlen. The characters rule below makes the
  // two counts agree for any value that passes, because only ASCII is allowed.
  if (Array.from(trimmed).length > PAYROLL_ID_MAX_LENGTH) {
    return 'too-long';
  }
  if (!PAYROLL_ID_ALLOWED_CHARACTERS.test(trimmed)) {
    return 'invalid-characters';
  }
  return null;
}

/**
 * True when a failed save message is one of the backend's Payroll ID refusals:
 * "Payroll ID is required", "Payroll ID must be 35 characters or fewer" and
 * "Payroll ID is already used by another team member".
 */
export function isPayrollIdServerMessage(message: unknown): message is string {
  return (
    typeof message === 'string' &&
    message.startsWith(PAYROLL_ID_SERVER_MESSAGE_PREFIX)
  );
}

/**
 * The Payroll ID to send to the server: trimmed when the Payroll ID rules are active, untouched
 * otherwise.
 */
export function payrollIdForSave<T>(value: T, isActive: boolean): T | string {
  return isActive && typeof value === 'string'
    ? normalisePayrollId(value)
    : value;
}
